import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { tenantAttrDefinitionApi } from '../api/tenantAttrDefinitionApi';

import { mergeTenantAttrSchema, MergedTenantAttrSchema } from './mergeTenantAttrSchema';
import { tenantAttrSchema } from './tenantAttrSchema';
import { tenantAttrCategories } from './tenantAttrCategories';

/** React Query key for the active (form-facing) definition list. */
export const TENANT_ATTR_DEFINITIONS_QUERY_KEY = ['tenant-attr-definitions', 'active'] as const;

/**
 * The tenant-attribute schema the authoring forms should render: the hard-coded
 * `tenantAttrSchema` plus any definitions registered in the portal
 * (config.tenant_attr_definition, mig 280).
 *
 * Fails soft in every direction — a backend without the route, an env without
 * migration 280, or a network error all collapse to the code schema, so the
 * Tenant Attributes form never depends on this call succeeding.
 */
export function useTenantAttrSchema(): MergedTenantAttrSchema & { isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: TENANT_ATTR_DEFINITIONS_QUERY_KEY,
    queryFn: () => tenantAttrDefinitionApi.list(false),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const merged = useMemo(
    () => mergeTenantAttrSchema(data?.definitions, tenantAttrSchema, tenantAttrCategories),
    [data?.definitions],
  );

  return { ...merged, isLoading };
}

export default useTenantAttrSchema;
